import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Alert,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    Link,
    useDialogClose,
} from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { apiClient, useContactSupport } from '../../state';
import { getDuplicateSpaceError } from './duplicateSpaceError';

type DuplicateSpaceDialogContentProps = {
    space: api.Space;
    siteContext?: {
        siteId: string;
        siteSpaceId: string;
        siteItemType: 'variant' | 'section';
        canAdminSite: boolean;
    };
};

/**
 * Content asking the user to confirm duplicating a space.
 */
export function DuplicateSpaceDialogContent(props: DuplicateSpaceDialogContentProps) {
    const { space, siteContext } = props;
    const [error, setError] = React.useState<string | null>(null);
    const contactSupport = useContactSupport();
    const closeDialog = useDialogClose();
    const itemType = siteContext?.siteItemType ?? 'space';

    return (
        <>
            <DialogHeader>
                <Heading>Duplicate {itemType}?</Heading>
            </DialogHeader>
            <DialogBody>
                This will create a copy of <strong>{space.title}</strong> and all of its content.
                {error ? (
                    <Alert
                        kind="danger"
                        title={`Unable to duplicate ${itemType}`}
                        details={
                            <>
                                {error} Please try again or{' '}
                                <Link
                                    onPress={() => {
                                        closeDialog();
                                        void contactSupport();
                                    }}
                                >
                                    contact support
                                </Link>
                            </>
                        }
                        className="mt-4"
                        size="small"
                    />
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <DuplicateSpaceButton space={space} siteContext={siteContext} onError={setError} />
            </DialogFooter>
        </>
    );
}

function DuplicateSpaceButton(
    props: DuplicateSpaceDialogContentProps & {
        onError: (error: string | null) => void;
    }
) {
    const { space, siteContext, onError } = props;
    const { navigate } = useNavigation();

    return (
        <DialogActionButton
            label="Duplicate"
            onPress={async (_event, { preventClose }) => {
                onError(null);

                try {
                    const duplicatedSpace = siteContext
                        ? (
                              await apiClient.siteSpaces.duplicateSiteSpace(
                                  space.organization,
                                  siteContext.siteId,
                                  siteContext.siteSpaceId,
                                  {
                                      draft: true,
                                  }
                              )
                          ).data.space
                        : (await apiClient.spaces.duplicateSpace(space.id)).data;

                    analytics.logEvent(
                        analytics.createSpaceEvent(duplicatedSpace, {
                            type: 'space_create',
                            source: 'duplicate',
                        })
                    );

                    navigate({
                        screen: {
                            type: 'space',
                            organization: duplicatedSpace.organization,
                            site: siteContext?.siteId,
                            space: duplicatedSpace.id,
                        },
                    });
                } catch (error) {
                    preventClose();
                    onError(getDuplicateSpaceError(error));
                }
            }}
        />
    );
}
